import { globalStore } from '@components/stores';
import { toastsStore } from '@components/ui/toasts';
import { type RecaptchaAction } from '@shared/types';

import type { Recaptcha, RecaptchaEnterprise } from './types';

import { browser } from '$app/environment';

let isAllowed: boolean | undefined;
let client: Recaptcha | undefined;
let bodyClass = '';
let isCommon: boolean | undefined;
let key: string | undefined;
let keyV2: string | undefined;

const initRecaptcha = () => {
    if (!isAllowed) {
        return;
    }

    return new Promise<Recaptcha | undefined>((resolve, reject) => {
        const script = document.createElement('script');

        if (key == null) {
            reject();
            return;
        }

        const scriptSrc = isCommon ?
            `https://www.google.com/recaptcha/api.js?render=${key}` :
            `https://www.google.com/recaptcha/enterprise.js?render=${key}`;
        script.async = true;
        script.src = scriptSrc;

        window.document.head.prepend(script);

        script.onload = () => {
            if (isCommon) {
                resolve(window.grecaptcha as Recaptcha);
            } else {
                resolve((window.grecaptcha as RecaptchaEnterprise).enterprise);
            }
        };

        script.onerror = (error) => {
            console.error('Failed to load recaptcha script', error);
            toastsStore.error('Failed to load recaptcha script. Please reload the page.');
            resolve(undefined);
        };
    });
};

const renderRecaptchaV2 = (container: string | HTMLElement, success: (token: string) => void, expired?: () => void) => {
    if (!isAllowed) {
        return;
    }

    return new Promise<string | undefined>((resolve, reject) => {
        if (client == null || keyV2 == null) {
            reject();
            return;
        }

        client.render(container, {
            sitekey: keyV2,
            theme: 'dark',
            callback: (token) => {
                success(token);
                resolve(token);
            },
            'error-callback': () => { reject(null); },
            'expired-callback': expired ?? (() => { reject(null); }),
        });
    });
};

const resetRecaptchaV2 = () => {
    if (!isAllowed) {
        return;
    }

    if (client) {
        client.reset();
    }
};

const getToken = async (action: RecaptchaAction): Promise<string | undefined> => {
    if (!isAllowed) {
        return Promise.resolve(undefined);
    }

    return new Promise<string>((resolve, reject) => {
        try {
            if (client == null) {
                throw new Error('Recaptcha is not initialized');
            }
            client.ready(async () => {
                const recaptchaToken = await client?.execute(key || '', {
                    action,
                });

                if (typeof recaptchaToken !== 'string') {
                    throw new Error('Type of \'recaptchaToken\' not a \'string\'');
                }
                resolve(recaptchaToken);
            });
        } catch (error) {
            reject(error);
        }
    });
};

const init = async () => {
    if (!isAllowed) {
        return;
    }

    if (
        isCommon == null ||
        !key ||
        !keyV2
    ) {
        toastsStore.error('Something went wrong, please try again');
        throw new Error('Failed init recaptcha: params not set.');
    }

    if (browser && !client) {
        client = await initRecaptcha();
    }
};

const setBodyClassName = (value: string) => {
    if (!isAllowed) {
        return;
    }

    bodyClass = value;
};

const show = async () => {
    if (!isAllowed) {
        return;
    }

    await init();
    globalStore.deleteBodyClass(bodyClass);
};

const hide = () => {
    if (!isAllowed) {
        return;
    }

    globalStore.addBodyClass(bodyClass);
};

const setConfig = (
    isFeatureAllowed: boolean,
    params: {
        isCommon: boolean;
        siteKey: string;
        siteKeyV2: string;
    }
) => {
    isAllowed = isFeatureAllowed;
    isCommon = params.isCommon;
    key = params.siteKey;
    keyV2 = params.siteKeyV2;
};

export const recaptchaService = {
    setConfig,
    init,
    renderRecaptchaV2,
    resetRecaptchaV2,
    getToken,
    setBodyClassName,
    show,
    hide,
};
